import styled from "styled-components";
import { Black, Black07, DarkGrey, OffWhite, White } from "./colors";
import React from "react";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

const StyledButton = styled.button`
  background-color: ${(props) => props.$background};
  color: ${(props) => props.$color || White};
  padding: 10px 24px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  font-size: 20px;
  border-radius: 200px;
  border: none;
  width: ${(props) => props.width};
  box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.24);
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    background-color: ${(props) => props.$background || Black07};
  }
`;

const StyledButtonMedium = styled.button`
  background-color: ${(props) => props.$background};
  color: ${(props) => props.$color || White};
  font-family: "Satoshi-Variable";
  font-weight: 900;
  padding: 8.5px 15px;
  font-size: 17px;
  border-radius: 200px;
  border: none;
  width: ${(props) => props.width};
  box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.24);
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    background-color: ${(props) => props.$background || Black07};
  }
`;

const StyledButtonSmall = styled.button`
  background-color: ${(props) => props.$background};
  color: ${(props) => props.$color || Black};
  padding: 7.5px 15px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  font-size: 13px;
  border: none;
  border-radius: 200px;
  width: ${(props) => props.width};
  box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.24);
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    background-color: ${(props) => props.$background || Black07};
  }
`;

const StyledButtonGradient = styled.button`
  background: ${(props) =>
    `linear-gradient(120deg, ${props.$background}, ${props.$transitionBackground})`};
  color: ${(props) => props.color || OffWhite};
  padding: 10px 24px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  margin-right: 10px;
  font-size: 20px;
  border-radius: 200px;
  border: none;
  width: ${(props) => props.width};
  box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.24);
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    background-color: ${DarkGrey};
  }
`;

const StyledButtonOutline = styled.button`
  padding: 10px 24px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  font-size: 20px;
  background-color: transparent;
  border: none;
  border-radius: 200px;
  color: ${(props) => props.$background};
  color: ${(props) => props.textOverride};
  width: ${(props) => props.width};
  box-shadow: inset 0px 0px 0px 3px ${(props) => props.$background};
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    color: ${(props) => props.$textHoverOverride};
    background: ${(props) => props.$background};
  }
`;

const StyledButtonOutlineMedium = styled.button`
  font-family: "Satoshi-Variable";
  font-weight: 900;
  padding: 8.5px 15px;
  font-size: 17px;
  background-color: transparent;
  border: none;
  border-radius: 200px;
  color: ${(props) => props.$background};
  color: ${(props) => props.textOverride};
  width: ${(props) => props.width};
  box-shadow: inset 0px 0px 0px 3px ${(props) => props.$background};
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    color: ${(props) => props.$textHoverOverride};
    background: ${(props) => props.$background};
  }
`;

const StyledButtonOutlineSmall = styled.button`
  padding: 7.5px 15px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  font-size: 13px;
  background-color: transparent;
  border: none;
  border-radius: 200px;
  color: ${(props) => props.$background};
  color: ${(props) => props.textOverride};
  width: ${(props) => props.width};
  box-shadow: inset 0px 0px 0px 3px ${(props) => props.$background};
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }
  &:hover {
    color: ${Black};
    color: ${(props) => props.textOverride};
    background: ${(props) => props.$background};
  }
`;

const StyledButtonOutlineGradientMedium = styled.button`
  padding: 8.5px 15px;
  font-family: "Satoshi-Variable";
  font-weight: 900;
  font-size: 17px;
  border-radius: 200px;
  width: ${(p) => p.width || "auto"};
  color: ${(p) => p.textOverride || (p.$onDark ? White : Black)};
  background: transparent;

  /* Gradient border that respects rounded corners */
  position: relative;
  z-index: 0;
  border: none;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px; /* border thickness */
    background: linear-gradient(
      120deg,
      ${(p) => p.$background},
      ${(p) => p.$transitionBackground || p.$trans}
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: -1;
  }

  &:hover {
    color: ${(p) => p.$textHoverOverride || (p.$onDark ? White : Black)};
  }
`;

export const withHaptics = (Component = ImpactStyle.Light) =>
  React.forwardRef(({ onClick, ...props }, ref) => {
    const handleClick = async (e) => {
      if (Capacitor.isNativePlatform()) {
        try {
          await Haptics.impact({ style: ImpactStyle.Light });
        } catch (err) {
          console.warn("Haptics failed:", err);
        }
      }

      if (onClick) onClick(e);
    };

    return <Component ref={ref} onClick={handleClick} {...props} />;
  });

export const Button = withHaptics(StyledButton);
export const ButtonMedium = withHaptics(StyledButtonMedium);
export const ButtonSmall = withHaptics(StyledButtonSmall);
export const ButtonGradient = withHaptics(StyledButtonGradient);
export const ButtonOutline = withHaptics(StyledButtonOutline);
export const ButtonOutlineMedium = withHaptics(StyledButtonOutlineMedium);
export const ButtonOutlineSmall = withHaptics(StyledButtonOutlineSmall);
export const ButtonOutlineGradientMedium = withHaptics(
  StyledButtonOutlineGradientMedium
);
